import { createContext, useContext, type Context, type ReactNode } from "react";

import { useReportsViewReportLayouts } from "@/app/app/_/views/ReportsView/ReportsView.hooks";

type ReportsViewReportLayoutContextValue = ReturnType<
  typeof useReportsViewReportLayouts
>;

// Pin the Context object on globalThis so Vite HMR cannot create a second
// createContext() instance while stale consumers still hold the old one.
const GLOBAL_CONTEXT_KEY =
  "__neoledger_ReportsViewReportLayoutContext__" as const;

type GlobalContextBag = typeof globalThis & {
  [GLOBAL_CONTEXT_KEY]?: Context<ReportsViewReportLayoutContextValue | null>;
};

const g = globalThis as GlobalContextBag;
if (!g[GLOBAL_CONTEXT_KEY]) {
  g[GLOBAL_CONTEXT_KEY] =
    createContext<ReportsViewReportLayoutContextValue | null>(null);
}

const ReportsViewReportLayoutContext = g[GLOBAL_CONTEXT_KEY]!;

export function ReportsViewReportLayoutProvider({
  children,
}: {
  children: ReactNode;
}) {
  const value = useReportsViewReportLayouts();
  return (
    <ReportsViewReportLayoutContext.Provider value={value}>
      {children}
    </ReportsViewReportLayoutContext.Provider>
  );
}

/** The layout context, or ``null`` outside the provider (report panel groups
 *  render in the node panel, which is only scoped on the reports route). */
export function useReportsViewReportLayoutContextOrNull() {
  return useContext(ReportsViewReportLayoutContext);
}

export function useReportsViewReportLayoutContext() {
  const ctx = useContext(ReportsViewReportLayoutContext);
  if (!ctx) {
    throw new Error(
      "useReportsViewReportLayoutContext requires ReportsViewReportLayoutProvider",
    );
  }
  return ctx;
}
